/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import React from 'react';
import { Globe, ShoppingCart, Zap, RefreshCw, Smartphone, Store, ExternalLink } from 'lucide-react';

export const StorefrontDoc = () => (
  <div className="space-y-8">
    <p className="text-lg text-stone-600 dark:text-stone-300 leading-relaxed font-medium">
        The Ghost Storefront is a turn-key e-commerce solution that allows merchants to instantly publish their inventory to a public-facing website. It handles product browsing, cart management, and multi-chain checkout without writing a single line of code.
    </p>

    <div className="grid gap-6 md:grid-cols-2">
        <div className="p-6 bg-white dark:bg-white/5 rounded-xl border border-stone-200 dark:border-white/10">
            <h4 className="font-bold text-stone-900 dark:text-white mb-2 flex items-center gap-2">
                <Globe size={18} className="text-nobel-gold"/> Instant Deployment
            </h4>
            <p className="text-sm text-stone-600 dark:text-stone-400 leading-relaxed">
                Your store is automatically generated based on your dashboard inventory. Any changes to products, prices, or stock levels are reflected instantly on your storefront URL (e.g., store.ghostpay.fun/your-shop).
            </p>
        </div>
        <div className="p-6 bg-white dark:bg-white/5 rounded-xl border border-stone-200 dark:border-white/10">
            <h4 className="font-bold text-stone-900 dark:text-white mb-2 flex items-center gap-2">
                <Zap size={18} className="text-nobel-gold"/> Web3 Checkout
            </h4>
            <p className="text-sm text-stone-600 dark:text-stone-400 leading-relaxed">
                Native support for Solana and Sui payments. The checkout process detects the user's wallet, switches chains if necessary, and executes the payment directly on-chain via our smart contracts.
            </p>
        </div>
    </div>

    <section>
        <h3 className="text-xl font-bold text-stone-900 dark:text-white mb-4">Capabilities</h3>
        <ul className="space-y-3">
            <li className="flex gap-3 text-sm text-stone-700 dark:text-stone-300 p-4 rounded-lg bg-stone-50 dark:bg-white/5 border border-stone-100 dark:border-white/5">
                <ShoppingCart size={20} className="text-stone-400 shrink-0" />
                <span><strong>Shopping Cart:</strong> Persistent local cart state allowing customers to batch multiple items into a single order with automated tax calculation.</span>
            </li>
            <li className="flex gap-3 text-sm text-stone-700 dark:text-stone-300 p-4 rounded-lg bg-stone-50 dark:bg-white/5 border border-stone-100 dark:border-white/5">
                <RefreshCw size={20} className="text-stone-400 shrink-0" />
                <span><strong>Real-time Stock:</strong> Prevents overselling by checking on-chain inventory state via GhostOS before transaction signature.</span>
            </li>
            <li className="flex gap-3 text-sm text-stone-700 dark:text-stone-300 p-4 rounded-lg bg-stone-50 dark:bg-white/5 border border-stone-100 dark:border-white/5">
                <Smartphone size={20} className="text-stone-400 shrink-0" />
                <span><strong>Mobile First:</strong> Fully responsive design that works seamlessly inside in-app browsers of mobile wallets like Phantom and Suiet.</span>
            </li>
        </ul>
    </section>

    <section className="bg-stone-900 dark:bg-[#1A1D23] rounded-2xl p-8 text-white relative overflow-hidden shadow-2xl border border-white/10">
        <div className="absolute top-0 right-0 w-64 h-64 bg-nobel-gold/10 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2"></div>
        <div className="relative z-10 flex flex-col md:flex-row items-center gap-8">
            <div className="flex-1">
                <h3 className="font-serif text-2xl font-bold mb-2">Experience the Live Demo</h3>
                <p className="text-stone-400 text-sm leading-relaxed mb-6 max-w-lg">
                    Try out a fully functional Ghost Storefront instance. Connect your wallet (Solana Devnet or Sui Devnet supported) and purchase a virtual coffee to test the end-to-end flow.
                </p>
                <a 
                    href="https://coffeeshop.ghostpay.fun" 
                    target="_blank" 
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black rounded-xl font-bold text-sm hover:bg-stone-200 transition-colors"
                >
                    <Store size={16} /> Open Coffee Shop Demo <ExternalLink size={14} className="opacity-50"/>
                </a>
            </div>
        </div>
    </section>
  </div>
);
